import { useCallback, useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ModuleKey } from '@gty/common';

import { usePermissions } from '../auth/usePermissions';
import {
  bookingsService,
  customersService,
  creditNotesService,
  dashboardService,
  invoicesService,
  leadsService,
  paymentsService,
  reportsService,
  toursService,
  vouchersService,
} from '../services';
import { CACHE } from './query-config';

/**
 * LOAD AHEAD OF THE CLICK.
 *
 * The reported problem — "the site feels slow" — was a loading STRATEGY
 * problem, not a query problem. Nothing started loading until the user clicked,
 * so every navigation paid the full ~1.2s round trip in full view, from a blank
 * screen. The backend cost is Railway's public proxy and is out of scope; what
 * is in scope is that the wait happens BEFORE the click instead of after it.
 *
 * Two layers, both here:
 *
 *  1. `usePrefetchOnLogin` — as soon as the shell mounts with a live session,
 *     warm the dashboard and the two screens people go to next.
 *  2. `usePrefetchModule` — warm a module the moment the pointer touches its
 *     nav item. Hover-to-click is ~200-400ms of free lead time, and the fetch
 *     and the lazy route chunk both start in it.
 *
 * Everything here is best-effort by construction. `prefetchQuery` resolves
 * rather than rejects on failure, so a prefetch that 403s or times out is
 * silently discarded and the real query on the real screen still runs, renders
 * its own error state, and is the thing the user actually sees. A prefetch can
 * make the app faster; it can never make it wrong.
 */

/**
 * The lazy route chunks, keyed by module.
 *
 * Data is only half the wait — the screen's JavaScript is a separate network
 * round trip that `React.lazy` also defers to click time. These are the SAME
 * dynamic imports the router uses, so warming one populates the module cache
 * the router then hits synchronously. Vite dedupes them; calling twice is free.
 */
const CHUNK: Partial<Record<ModuleKey, () => Promise<unknown>>> = {
  DASHBOARD: () => import('../dashboard/DashboardPage'),
  LEAD_CRM: () => import('../leads/LeadsPage'),
  CUSTOMER_CRM: () => import('../customers/CustomersPage'),
  TOUR_MASTER: () => import('../tours/ToursPage'),
  BOOKING_MANAGEMENT: () => import('../bookings/BookingsPage'),
  INVOICE_GENERATOR: () => import('../invoices/InvoicesPage'),
  BOOKING_VOUCHER: () => import('../vouchers/VouchersPage'),
  CREDIT_NOTES: () => import('../credit-notes/CreditNotesPage'),
  PAYMENTS_RECEIPTS: () => import('../payments/PaymentsPage'),
  REPORTS_ANALYTICS: () => import('../reports/ReportsPage'),
};

/**
 * What each module's landing screen reads on arrival.
 *
 * Deliberately ONLY the list/summary queries a screen issues on mount — never a
 * detail record, because which record you will open is unknowable and guessing
 * would fetch rows nobody asked for.
 *
 * NOTE ON THE EMPTY DATABASE: every one of these resolves to `[]` or a
 * zeroed summary against the current data, and that is a completely ordinary
 * outcome here. An empty array is a valid cached value — the screen renders its
 * empty state instantly from cache instead of flashing a skeleton first. No
 * caller of this file indexes into a prefetched array.
 */
function warmModule(client: QueryClient, module: ModuleKey): void {
  const prefetch = <T>(key: readonly unknown[], fn: (signal?: AbortSignal) => Promise<T>, policy: { staleTime: number }): void => {
    void client.prefetchQuery({
      queryKey: key,
      queryFn: ({ signal }) => fn(signal),
      staleTime: policy.staleTime,
    });
  };

  switch (module) {
    case 'DASHBOARD':
      prefetch(['dashboard', 'summary'], (s) => dashboardService.getSummary(s), CACHE.money);
      break;
    case 'LEAD_CRM':
      prefetch(['leads', 'list'], (s) => leadsService.list(s), CACHE.workList);
      break;
    case 'CUSTOMER_CRM':
      prefetch(['customers', 'list'], (s) => customersService.list(s), CACHE.workList);
      break;
    case 'TOUR_MASTER':
      prefetch(['tours', 'list'], (s) => toursService.list(s), CACHE.reference);
      break;
    case 'BOOKING_MANAGEMENT':
      prefetch(['bookings', 'list'], (s) => bookingsService.list(s), CACHE.workList);
      break;
    case 'INVOICE_GENERATOR':
      prefetch(['invoices', 'list'], (s) => invoicesService.list(s), CACHE.workList);
      prefetch(['invoices', 'summary'], (s) => invoicesService.summary(s), CACHE.money);
      break;
    case 'BOOKING_VOUCHER':
      prefetch(['vouchers', 'list'], (s) => vouchersService.list(s), CACHE.workList);
      break;
    case 'CREDIT_NOTES':
      prefetch(['credit-notes', 'list'], (s) => creditNotesService.list(s), CACHE.workList);
      break;
    case 'PAYMENTS_RECEIPTS':
      prefetch(['payments', 'list'], (s) => paymentsService.list(s), CACHE.workList);
      prefetch(['payments', 'summary'], (s) => paymentsService.summary(s), CACHE.money);
      break;
    case 'REPORTS_ANALYTICS':
      prefetch(['reports', 'summary'], (s) => reportsService.summary(s), CACHE.money);
      break;
    default:
      // SETTINGS_ROLES and anything added later: no prefetch. Settings is a
      // destination people navigate to on purpose and rarely, so warming it
      // would spend a request on a screen almost nobody is about to open.
      break;
  }
}

/**
 * Warm the landing screen and the likely-next ones, once, after login.
 *
 * Ordered by how likely the next click is, and kept SHORT on purpose. Every
 * entry here is a request that competes with the screen the user is actually
 * looking at, so this is three modules, not eleven — the rest are covered by
 * hover prefetch, which costs nothing until there is evidence of intent.
 *
 * Gated on permission. Prefetching a module the role cannot read would fire a
 * guaranteed 403 on every login for restricted roles: harmless to the UI, but
 * it would fill the server log with denials that look like an attack.
 */
export function usePrefetchOnLogin(): void {
  const client = useQueryClient();
  const { allows } = usePermissions();

  useEffect(() => {
    const wanted: ModuleKey[] = ['DASHBOARD', 'LEAD_CRM', 'BOOKING_MANAGEMENT'];

    for (const module of wanted) {
      if (!allows(module, 'VIEW')) continue;
      warmModule(client, module);
      void CHUNK[module]?.();
    }
    // `allows` is stable per permission map, so this re-runs only if the user's
    // permissions actually change — not on every render and not on navigation.
  }, [client, allows]);
}

/**
 * Prefetch a module on pointer intent (hover / focus of its nav item).
 *
 * Returns a stable callback so the sidebar can hand it straight to
 * `onMouseEnter` without re-rendering the nav on every pointer move.
 */
export function usePrefetchModule(): (module: ModuleKey) => void {
  const client = useQueryClient();
  const { allows } = usePermissions();

  return useCallback(
    (module: ModuleKey): void => {
      if (!allows(module, 'VIEW')) return;
      warmModule(client, module);
      void CHUNK[module]?.();
    },
    [client, allows],
  );
}
